import {
  Checkbox,
  CircularProgress,
  Collapse,
  FormHelperText,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  Stack,
  Switch,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, ListFilter, LucideSearch, X } from "lucide-react";
import { useMemo, useState, type ChangeEvent, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  CourseRow,
  PanelHeader,
  ResultsList,
  RowSeparator,
  SearchBox,
  SearchInput,
  SelectorPanel,
  TypeChip
} from "./ExemptedCourseSelector.styles";
import type { ExemptedCourseSelectorProps } from "./ExemptedCourseSelector.types";
import {
  EXEMPTED_COURSE_TYPES,
  filterSelectableCourses,
  getExemptedCourseTypeLabel
} from "./ExemptedCourseSelector.utils";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { useExemptableLearningObjectsQuery } from "../../queries/externalTrainingSkillbook";
import type { ExemptedCourseType } from "../../types";

export function ExemptedCourseSelector({
  enabled,
  onEnabledChange,
  value,
  onChange,
  error,
  readOnly = false
}: ExemptedCourseSelectorProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [typeFilters, setTypeFilters] = useState<ExemptedCourseType[]>([]);
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);

  const { data, isError, isLoading } = useExemptableLearningObjectsQuery({ enabled });

  const results = useMemo(
    () => filterSelectableCourses(data ?? [], typeFilters, searchText),
    [data, searchText, typeFilters]
  );

  const rows = value && !results.some((course) => course.id === value.id)
    ? [value, ...results]
    : results;

  const handleToggle = (checked: boolean) => {
    onEnabledChange(checked);
    if (!checked) onChange(null);
  };

  const toggleTypeFilter = (filter: ExemptedCourseType) => {
    setTypeFilters((prev) => (
      prev.includes(filter) ? prev.filter((item) => item !== filter) : [...prev, filter]
    ));
  };

  const hint = (message: string) => (
    <Typography color={ theme.customColors.textDisabledAlternative } fontSize="0.875rem">
      { message }
    </Typography>
  );

  return (
    <div>
      <Stack alignItems="center" direction="row" flexWrap="wrap" gap="1rem">
        <Typography color={ theme.customColors.textPrimary } fontWeight={ 700 }>
          { t("exempted_course_toggle") }
        </Typography>
        <Stack alignItems="center" direction="row" gap="0.5rem">
          <Typography color={ theme.customColors.textPrimary }>{ t("exempted_course_no") }</Typography>
          <Switch
            checked={ enabled }
            disabled={ readOnly }
            inputProps={ { "aria-label": t("exempted_course_toggle") } }
            onChange={ (event: ChangeEvent<HTMLInputElement>) => handleToggle(event.target.checked) }
            sx={ {
              "& .MuiSwitch-switchBase.Mui-checked": { color: theme.customColors.backgroundPrimaryCta },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: theme.customColors.backgroundPrimaryCta,
                opacity: 0.6
              }
            } }
          />
          <Typography color={ theme.customColors.textPrimary }>{ t("exempted_course_yes") }</Typography>
        </Stack>
      </Stack>

      { enabled ? (
        <SelectorPanel>
          <PanelHeader
            aria-expanded={ expanded }
            onClick={ () => setExpanded((prev) => !prev) }
            type="button"
          >
            { value ? (
              <Stack alignItems="center" direction="row" gap="0.75rem">
                <Typography color={ theme.customColors.textPrimary } fontSize="1.125rem" fontWeight={ 700 }>
                  { value.title || `#${value.id}` }
                </Typography>
                { value.type ? <TypeChip>{ getExemptedCourseTypeLabel(value.type, t) }</TypeChip> : null }
              </Stack>
            ) : (
              <Typography color={ theme.customColors.textDisabledAlternative } fontSize="1.125rem">
                { t("exempted_course_select") }
              </Typography>
            ) }
            <ChevronDown
              aria-hidden="true"
              color={ theme.customColors.borderAccent }
              size="1.5rem"
              style={ {
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform .3s"
              } }
            />
          </PanelHeader>

          <Collapse in={ expanded }>
            <Stack gap="1rem">
              <Stack alignItems="center" direction="row" gap="1rem">
                <SearchBox>
                  <LucideSearch aria-hidden="true" color={ theme.customColors.backgroundPrimaryCta } size={ 20 } />
                  <SearchInput
                    disabled={ readOnly }
                    inputProps={ { "aria-label": t("search_training") } }
                    onChange={ (event: ChangeEvent<HTMLInputElement>) => setSearchText(event.target.value) }
                    placeholder={ t("search_training") }
                    value={ searchText }
                  />
                  { searchText ? (
                    <IconButton
                      aria-label={ t("empty_field") }
                      onClick={ () => setSearchText("") }
                      size="small"
                    >
                      <X aria-hidden="true" color={ theme.customColors.textTag } size={ 16 } />
                    </IconButton>
                  ) : null }
                </SearchBox>
                <ButtonDigited
                  aria-expanded={ Boolean(filtersAnchor) }
                  aria-haspopup="menu"
                  onClick={ (event: MouseEvent<HTMLElement>) => setFiltersAnchor(event.currentTarget) }
                  typology="filter"
                >
                  <>
                    { t("filters") }
                    <ListFilter aria-hidden="true" size={ 16 } />
                  </>
                </ButtonDigited>
                <Menu
                  anchorEl={ filtersAnchor }
                  onClose={ () => setFiltersAnchor(null) }
                  open={ Boolean(filtersAnchor) }
                >
                  { EXEMPTED_COURSE_TYPES.map((filter) => (
                    <MenuItem key={ filter } onClick={ () => toggleTypeFilter(filter) }>
                      <Checkbox checked={ typeFilters.includes(filter) } tabIndex={ -1 } />
                      <ListItemText primary={ getExemptedCourseTypeLabel(filter, t) } />
                    </MenuItem>
                  )) }
                </Menu>
              </Stack>

              { isLoading ? <CircularProgress size={ 20 } /> : null }
              { isError ? hint(t("exempted_course_search_error")) : null }
              { !isLoading && !isError && rows.length === 0 ? hint(t("exempted_course_no_results")) : null }

              { rows.length > 0 ? (
                <ResultsList aria-label={ t("exempted_course_select") } role="radiogroup">
                  { rows.map((course) => {
                    const selected = value?.id === course.id;
                    return (
                      <CourseRow key={ course.id } $selected={ selected }>
                        <Radio
                          checked={ selected }
                          disabled={ readOnly }
                          inputProps={ { "aria-label": course.title } }
                          name="exemptedCourse"
                          onChange={ () => onChange(course) }
                          sx={ { color: theme.customColors.textPrimary, padding: 0 } }
                          value={ course.id }
                        />
                        <RowSeparator />
                        <Typography color={ theme.customColors.textPrimary } fontWeight={ 700 }>
                          { course.title || `#${course.id}` }
                        </Typography>
                        { course.type ? <TypeChip>{ getExemptedCourseTypeLabel(course.type, t) }</TypeChip> : null }
                      </CourseRow>
                    );
                  }) }
                </ResultsList>
              ) : null }
            </Stack>
          </Collapse>

          { error ? <FormHelperText error>{ error }</FormHelperText> : null }
        </SelectorPanel>
      ) : null }
    </div>
  );
}
